/**
 * agentChatProtocol — 백엔드 에이전트(saas-ai-agent)가 emit 하는 **action verb** 어휘.
 *
 * ⚠️ 이것은 화면 지식이 아니라 챗↔에이전트 사이의 계약이다. 그래서 어댑터로 주입받지 않고
 *   여기 상수로 둔다. 화면이 주입하는 구조로 만들면 방향이 거꾸로가 된다 —
 *   화면은 이 어휘를 "받아서 처리"할 뿐, 정의하지 않는다.
 *
 * 상수화한 이유는 grep 가능성이다. 이전에는 문자열 리터럴이 패널 곳곳에 흩어져 있어
 *   백엔드가 verb 를 하나 추가·개명할 때 프론트 대응 지점을 찾기 어려웠다.
 *
 * 대응 백엔드: saas-ai-agent/src/chat/chat_actions.py
 */

export const ACTION = {
  /** 파라미터 값 변경 (role 미지정이면 대상 되묻기) */
  SET_PARAM:            "set_param",
  /** 엔진 실행 */
  RUN_ENGINE:           "run_engine",
  /** 표준 시나리오(템플릿) 선택 → 카드 추가 */
  SELECT_TEMPLATE:      "select_template",
  /** 전문가 사례 적용 */
  APPLY_EXPERT_CASE:    "apply_expert_case",
  /** 커스텀 시나리오 초안 등록(쓰기) */
  SAVE_CUSTOM_SCENARIO: "save_custom_scenario",
  /** 저장된 커스텀 시나리오 적용 */
  APPLY_CUSTOM_SAVED:   "apply_custom_saved",
  /** 커스텀 시나리오 추가 */
  ADD_CUSTOM_SCENARIO:  "add_custom_scenario",
  /** 구성 방식(AI 추천/표준 선택) 전환 */
  SELECT_MODE:          "select_mode",
  /** 시나리오 선택(비교→확정 단계) */
  SELECT_SCENARIO:      "select_scenario",
  /** 시나리오 선택 해제 */
  DESELECT_SCENARIO:    "deselect_scenario",
  /** 시나리오 카드 제거 */
  REMOVE_SCENARIO:      "remove_scenario",
  /** 계획 확정(승격/릴리즈) */
  CONFIRM:              "confirm",
  /** 단계 이동 */
  NAVIGATE:             "navigate",
  /**
   * 메뉴 화면(탭) 이동 — payload { key, name }. 시나리오 4단계 이동(NAVIGATE)과 별개다.
   *   실행은 프론트 로컬(requestScreenNav → App 의 plannel:navScreen)이고,
   *   **자동 실행하지 않는다** — 작업 중인 화면이 예고 없이 바뀌지 않도록 항상 클릭을 받는다.
   *
   * ⚠️ **정본은 이 verb 가 아니라 응답 top-level 의 `screen_links` 다.**
   *   백엔드 chat_actions.promote_nav_screen 이 검증을 통과한 nav_screen 을 actions/choices
   *   에서 걷어내 링크로 승격한다 — 화면 이동이 선택지 패널에 뜨면 "골라서 답하라"는 요구로
   *   읽혀 조회만 원한 사용자를 붙잡기 때문이다(말풍선 하단 링크 칩으로 낸다).
   *   여기 남는 경로는 구버전 백엔드·복원된 옛 턴을 위한 안전망이다.
   */
  NAV_SCREEN:           "nav_screen",
  /** 변경 되돌리기 */
  RESET_SCENARIO:       "reset_scenario",
  /** 자동 수행(Auto-Pilot) 시작 */
  AUTO_FLOW:            "auto_flow",
  /** 화면 액션 레지스트리 id 실행 */
  DISPATCH:             "dispatch",
};

/**
 * 되돌리기 어려운 verb — 챗이 **절대 자동 실행하지 않고** 반드시 사용자 클릭을 받는다.
 *   화면 레지스트리 id 기준 판정은 어댑터(isIrreversibleId)가 따로 담당한다(이중 안전망).
 */
export const IRREVERSIBLE_VERBS = new Set([ACTION.CONFIRM, ACTION.REMOVE_SCENARIO]);

/**
 * 액션 출처 — 백엔드가 디스크립터에 찍어 보내는 태그(`origin`).
 *
 * 같은 디스크립터라도 **사용자가 시킨 것**과 **모델이 곁들인 제안**은 다르게 다뤄야 한다.
 *   LLM 이 답변 끝에 흐름 제안으로 붙인 이동을 자동 실행하면 사용자가 화면 밖으로 끌려간다.
 *   반대로 "다음 단계"라고 말해서 나온 fast-path 이동까지 클릭을 요구하면 조작이 늘어난다.
 *
 * 대응 백엔드: saas-ai-agent/src/chat/chat_actions.py 의 ORIGIN_* / stamp_origin()
 */
export const ACTION_ORIGIN = {
  /** ```action / ```choices 펜스 — 모델이 만든 것 */
  LLM:      "llm",
  /** 발화를 결정형으로 해석한 분기(LLM 미경유) — 사용자가 그렇게 말한 것 */
  FASTPATH: "fastpath",
  /** 칩 클릭이 보낸 구조화 의도 — 이미 확정된 사용자 지시 */
  HINT:     "hint",
  /** 서버가 도구 산출물을 근거로 직접 첨부(커스텀 시나리오 등록 칩 등) */
  SERVER:   "server",
};

/**
 * 챗 HTTP 오류 코드 → 사용자 문구 키.
 *
 * 백엔드는 detail 을 영문 상수로 내린다(사용자 문구는 화면 언어 소유). 코드를 그대로
 *   노출하면 내부 식별자가 사용자에게 보이므로, 아는 코드는 문구로 바꾸고 모르는 코드는
 *   호출부 기본 문구로 떨어뜨린다.
 * 대응 백엔드: saas-ai-agent/src/api/routes/chat.py · chat_sessions.py
 */
export const CHAT_ERROR_KEYS = {
  SESSION_NOT_OWNER:     "MSG_PermissionDenied",
  CHAT_FAILED:           "MSG_ServerResponseFailed",
  UNSUPPORTED_MODULE:    "MSG_ActionUnavailableHere",
  SESSION_DELETE_FAILED: "agentChatDeleteError",
  TRACE_TURN_FAILED:     "agentTraceLoadFail",
  TURN_NOT_FOUND:        "agentTraceLoadFail",
  INVALID_RATING:        "MSG_ApplyFailed",
};

/** axios 오류에서 detail 코드를 읽어 문구로 바꾼다 — 모르는 코드·코드 없음이면 "". */
export function chatErrorText(t, err) {
  const detail = err?.response?.data?.detail ?? err?.detail;
  const code = typeof detail === "string" ? detail.trim()
    : String(detail?.code || detail?.error_code || "").trim();
  const key = CHAT_ERROR_KEYS[code];
  return (t && key) ? t(key) : "";
}

/** 커스텀 시나리오 초안 편집 선택지가 쓰는 의사 액션(백엔드가 아니라 챗 내부 흐름) */
export const DRAFT_ACTION = {
  EDIT_PARAMS: "edit_custom_scenario_params",
  PARAM_PICK:  "draft_param_pick",
  PARAM_SET:   "draft_param_set",
  PARAM_DONE:  "draft_param_done",
};

export default ACTION;
